import { isDefined } from '@sgme/fp';
import { Button, Icon, Tooltip } from '@sgme/ui';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { BidAskInput } from '@/App/tiles/Option/strategy/fields/BidAskInput.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import type { LegsData, OptionLegProperties } from '@/store/option/state.ts';
import type { BidAsk } from '@/types/common.ts';

export function Volatility({
  leg,
  legData,
  onChange,
}: {
  leg: OptionLegProperties;
  legData: LegsData | undefined;
  onChange: (bidAsk: BidAsk<number | null>) => void;
}) {
  const volatilityFromQuote = legData?.marketData?.volatility;
  const {
    currentUser: { userType },
  } = useUserContext();
  const { formatNumber } = useIntl();

  const isInternalSales = userType === 'Internal Sales';
  const suffix =
    isInternalSales && isDefined(legData?.midVolatility?.smiledVolatilityMid) ? (
      <span data-e2e={'midSmiledVolatility-text'}>
        M: <FormattedNumber value={legData.midVolatility.smiledVolatilityMid} minimumFractionDigits={3} />
      </span>
    ) : null;

  function handleUnpin() {
    onChange({ bid: null, ask: null });
  }

  return (
    <div className="d-flex flex-grow-1 flex-center">
      <BidAskInput
        editMode="bidAsk"
        data-e2e="volatility"
        side={leg.side}
        format={(value) => (isDefined(value) ? formatNumber(value, { minimumFractionDigits: 3 }) : '')}
        value={{
          bid: leg.volatilityBid ?? volatilityFromQuote?.bid,
          ask: leg.volatilityAsk ?? volatilityFromQuote?.ask,
        }}
        suffix={suffix}
        onChange={onChange}
      />
      {isDefined(leg.volatilityBid) && isDefined(leg.volatilityAsk) && (
        <Tooltip contents={<FormattedMessage id="option.pin.tooltip.message" />}>
          <Button onClick={handleUnpin} data-e2e="pin-button" size="sm" purpose="flat" iconPosition="start">
            <Icon icon="push_pin" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
}
